import React, { useEffect, useState } from "react";
import logo from "../assets/scenathon_logo_short.png";
import { Link, useLocation, useNavigate } from "react-router-dom";

import iconResultados from "../assets/icons/clipboard-text.svg";
import iconPathways from "../assets/icons/pathways.svg";
import iconScenarios from "../assets/icons/scenarios.svg";
import iconLogout from "../assets/icons/logout.svg";
import iconAdmin from "../assets/icons/admin.svg";
import iconChat from "../assets/chatbot.png";
import { useAuth } from "../auth/AuthContext";
import { useRole } from "../auth/useRole";
import { ShowIfHasSection, ShowIfRole } from "../auth/RoleComponents";
import TutorialOverlay, { useTutorial, DISCLAIMER_KEY } from "./TutorialOverlay";

const API = process.env.REACT_APP_API_URL;
const SIDEBAR_W = "w-[302px]";

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { SECTIONS } = useRole();
  const navigate = useNavigate();
  const { logout, accessToken, csrfToken, user } = useAuth();
  const { show: showTutorial, skipDisclaimer, open: openTutorial, close: closeTutorial } = useTutorial(user);

  const isActive = (path) => location.pathname.startsWith(path);
  const close = () => setOpen(false);
  const [lastLocationKey, setLastLocationKey] = useState(null);

  useEffect(() => {
    if (!location.state?.openTutorial) return;
    if (location.key && location.key === lastLocationKey) return;

    setLastLocationKey(location.key);
    openTutorial();
  }, [location.key, location.state?.openTutorial, lastLocationKey, openTutorial]);

  const handleLogout = async () => {
    sessionStorage.removeItem(DISCLAIMER_KEY); // clear so disclaimer shows on next login
    try {
      await logout?.();
    } catch (e) {
      console.warn("AuthContext logout failed:", e);
      try {
        await fetch(`${API}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({}),
        });
      } catch (err) {
        console.warn("Logout endpoint failed:", err);
      }
    } finally {
      close();
      navigate("/", { replace: true });
    }
  };

  const linkCls = (path) =>
    `group flex items-center gap-3 text-left py-3 px-4 rounded-xl border font-medium transition-all duration-200 ${isActive(path)
      ? "bg-[#1E5B4F]/90 border-[#C49A1F]/70 text-white shadow-[0_10px_28px_rgba(30,91,79,0.35)]"
      : "bg-white/40 border-white/45 text-slate-800 hover:bg-white/60 hover:border-[#C49A1F]/50 hover:-translate-y-0.5"
    }`;

  const iconCls = (path) =>
    `w-5 h-5 transition duration-200 ${isActive(path) ? "brightness-0 invert" : "group-hover:brightness-75"}`;

  return (
    <>
      {/* ✅ Hamburger SOLO mobile y se oculta al abrir */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`lg:hidden fixed top-4 left-4 z-[60] h-10 w-10 rounded-xl bg-white/75 backdrop-blur-md shadow-lg ring-1 ring-white/70 items-center justify-center transition duration-200 hover:bg-white ${open ? "hidden" : "flex"
          }`}
        aria-label="Open menu"
      >
        <span className="text-xl leading-none">☰</span>
      </button>

      {/* ✅ Overlay SOLO mobile */}
      <div
        onClick={close}
        className={`lg:hidden fixed inset-0 z-50 bg-black/40 transition-opacity ${open ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
      />

      {/* ✅ Sidebar: desktop fixed; mobile drawer */}
      <aside
        className={`
          ${SIDEBAR_W}
          bg-white/35 border-r border-white/45 backdrop-blur-xl shadow-[0_24px_60px_rgba(15,23,42,0.22)] p-5 md:p-6 flex flex-col overflow-hidden
          h-[calc(100vh-64px)] fixed top-[64px] left-0 z-[55]

          transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        <div className="relative flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-28 w-28 grid place-items-center">
              <img src={logo} alt="logo" className="w-28 object-contain" />
            </div>
            <div>
              <p className="text-[11px] tracking-[0.16em] uppercase text-emerald-700 font-semibold">Scenathon Lab</p>
            </div>
          </div>

          {/* ✅ Cerrar SOLO mobile */}
          <button
            type="button"
            onClick={close}
            className="lg:hidden absolute right-0 top-0 h-9 w-9 rounded-xl bg-white/55 hover:bg-white/80 transition duration-200 flex items-center justify-center"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <nav
          className="mt-10 pt-1 flex flex-col gap-2.5 flex-1 overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <ShowIfHasSection section={SECTIONS.PATHWAYS}>
            <Link
              to="/pathways"
              data-tour="pathways"
              onClick={close}
              className={`${linkCls("/pathways")} py-3.5 text-[15px]`}
            >
              <img
                src={iconPathways}
                alt="icon"
                className={iconCls("/pathways")}
              />
              Pathway Definition
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.SCENARIOS}>
            <Link
              to="/scenarios"
              data-tour="scenarios"
              onClick={close}
              className={linkCls("/scenarios")}
            >
              <img
                src={iconScenarios}
                alt="icon"
                className={iconCls("/scenarios")}
              />
              Scenario Definition
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.RESULTS}>
            <Link to="/results" data-tour="results" onClick={close} className={linkCls("/results")}>
              <img
                src={iconResultados}
                alt="icon"
                className={iconCls("/results")}
              />
              Pathway Results
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.COMPARISON}>
            <Link
              to="/comparison"
              data-tour="comparison"
              onClick={close}
              className={linkCls("/comparison")}
            >
              <img
                src={iconScenarios}
                alt="icon"
                className={iconCls("/comparison")}
              />
              Pathway Comparison
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.AI_ASSISTANT}>
          <Link
            to="/ai-assistant"
            data-tour="ai-assistant"
            onClick={close}
            className={linkCls("/ai-assistant")}
          >
            <img
              src={iconChat}
              alt="icon"
              className={iconCls("/ai-assistant")}
            />
            AI Assistant
          </Link>
          </ShowIfHasSection>

        </nav>


        {/* Navigation Tour trigger */}
        <button
          type="button"
          onClick={() => { close(); openTutorial(); }}
          className="mt-2 flex items-center gap-3 text-left py-2.5 px-4 rounded-xl border border-white/45 bg-white/30 text-slate-500 text-sm font-medium transition-all duration-200 hover:bg-white/55 hover:border-[#C49A1F]/45 hover:text-slate-700"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
          </svg>
          Explore PPL
        </button>

        <ShowIfRole requiredRoles="admin">
          <Link to="/admin"
            onClick={close}
            className={`${linkCls("/admin")} mt-2`}>
            <img src={iconAdmin} alt="admin" className={iconCls("/admin")} />
            Admin PPL</Link>
        </ShowIfRole>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 flex items-center gap-3 text-left py-3 px-4 rounded-xl border border-white/45 bg-white/40 text-slate-800 font-medium transition-all duration-200 hover:bg-white/60 hover:border-[#C49A1F]/50"
        >
          <img src={iconLogout} alt="logout" className="w-5 h-5" />
          Logout
        </button>
      </aside>

      {/* Tutorial overlay */}
      {showTutorial && <TutorialOverlay skipDisclaimer={skipDisclaimer} onClose={closeTutorial} />}
    </>
  );
}
